import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShareViaEmail = ({ open, onOpenChange, subject, content, contentType }) => {
  const { token } = useAuth();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!email) {
      toast.error('Please enter an email address');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/emails/send`, {
        to_email: email,
        subject: subject,
        content: content
      }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success(`${contentType || 'Content'} sent to ${email}`);
      setEmail('');
      onOpenChange(false);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to send email';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="share-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#002FA7]" />
            Share via Email
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-[#9CA3AF] uppercase tracking-wider">Sending</Label>
            <p className="text-sm font-medium text-[#0A0A0A] mt-1 truncate">{subject}</p>
          </div>
          <div>
            <Label>Recipient Email *</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="recipient@example.com"
              data-testid="share-email-input"
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            />
          </div>
          <div className="bg-[#F3F4F6] rounded p-3 max-h-32 overflow-y-auto">
            <p className="text-xs text-[#9CA3AF] mb-1">Preview:</p>
            <p className="text-xs text-[#4B5563] whitespace-pre-wrap line-clamp-4">{content?.slice(0, 300)}{content?.length > 300 ? '...' : ''}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleSend} disabled={sending} data-testid="share-send-btn">
            {sending ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Send Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareViaEmail;
